import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from "@mui/material/Button";
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';

import { connect } from "react-redux";
import {
  updateUserToken,
  updateRefreshToken,
  updateUserId,
} from '../../redux/actions/userDataActions';

import * as Api from '../../api/index';

import '../../assets/css/custom.css';

import Logo from '../../assets/images/new_logo.png';
import { ReactComponent as Lock } from '../../assets/images/lock.svg';
import { ReactComponent as Email } from '../../assets/images/email.svg';
import AlertMsg from "../../components/AlertMsg";

const Login = (props) => {
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [email, setEmail] = useState('');
  const [pswd, setPswd] = useState('');
  const [msgAlert, setMsgAlert] = useState({ open: false, message: '', msgType: 'error' });

  const navigate = useNavigate();

  const onCloseAlertMsg = () => {
    setMsgAlert({ open: false, message: '', msgType: 'error' });
  };

  const onLogin = (e) => {
    setMsgAlert({ open: false, message: '', msgType: 'error' });
    e.preventDefault();
    let data = JSON.stringify({ email, password: pswd });
    setBtnDisabled(true);
    Api.login(data).then((response) => {
      if (response.success) {
        props.updateRefreshToken(response.data.refreshToken);
        props.updateUserToken(response.data.token);
        props.updateUserId(response.data?.user?._id ?? "");
        navigate("/");
      } else {
        setBtnDisabled(false);
        setMsgAlert({ open: true, message: response.msg, msgType: 'error' });
      }
    });
  };

  return (
    <div style={styles.root}>
      {/* Left branding panel */}
      <div style={styles.leftPanel}>
        <div style={styles.leftContent}>
          <img src={Logo} alt="Foodo" style={styles.leftLogo} />
          <Typography style={styles.leftHeading}>
            All your restaurants,<br />one powerful dashboard.
          </Typography>
          <Typography style={styles.leftSubtext}>
            Manage menus, orders, and settings across every location — faster than ever.
          </Typography>
          <div style={styles.divider} />
          <div style={styles.statsRow}>
            <div style={styles.statItem}>
              <Typography style={styles.statNum}>500+</Typography>
              <Typography style={styles.statLabel}>Restaurants</Typography>
            </div>
            <div style={styles.statItem}>
              <Typography style={styles.statNum}>99.9%</Typography>
              <Typography style={styles.statLabel}>Uptime</Typography>
            </div>
            <div style={styles.statItem}>
              <Typography style={styles.statNum}>24/7</Typography>
              <Typography style={styles.statLabel}>Support</Typography>
            </div>
          </div>
        </div>
        <div style={styles.leftGlowOrb1} />
        <div style={styles.leftGlowOrb2} />
      </div>

      {/* Right form panel */}
      <div style={styles.rightPanel}>
        <div style={styles.formCard}>
          <Typography style={styles.formWelcome}>Welcome back</Typography>
          <Typography style={styles.formSubtitle}>
            Sign in to your admin account
          </Typography>

          <form onSubmit={onLogin} style={styles.form}>
            <div style={styles.fieldWrapper}>
              <Typography style={styles.fieldLabel}>Email address</Typography>
              <TextField
                id="email"
                type="email"
                fullWidth
                required
                placeholder="you@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email height={16} width={16} style={{ opacity: 0.45 }} />
                    </InputAdornment>
                  ),
                  style: styles.inputBase,
                }}
                sx={inputSx}
              />
            </div>

            <div style={styles.fieldWrapper}>
              <Typography style={styles.fieldLabel}>Password</Typography>
              <TextField
                id="password"
                type="password"
                fullWidth
                required
                placeholder="Enter your password"
                value={pswd}
                onChange={e => setPswd(e.target.value)}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock height={16} width={16} style={{ opacity: 0.45 }} />
                    </InputAdornment>
                  ),
                  style: styles.inputBase,
                }}
                sx={inputSx}
              />
            </div>

            {msgAlert.msgType === 'error' && msgAlert.message ? (
              <div style={styles.errorBox}>
                <Typography style={styles.errorText}>{msgAlert.message}</Typography>
              </div>
            ) : null}

            <Button
              type="submit"
              variant="contained"
              disabled={btnDisabled}
              fullWidth
              style={styles.submitBtn}
              disableElevation
            >
              {btnDisabled
                ? <><CircularProgress size={14} style={{ color: '#fff', marginRight: 8 }} />Signing in…</>
                : 'Sign in'}
            </Button>
          </form>
        </div>

        <Typography style={styles.copyright}>
          © {new Date().getFullYear()} Foodo. All rights reserved.
        </Typography>
      </div>

      {msgAlert.msgType !== 'error' ?
        <AlertMsg msgAlert={msgAlert} onCloseAlertMsg={onCloseAlertMsg} /> : null}
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  updateUserToken: (data) => dispatch(updateUserToken(data)),
  updateRefreshToken: (data) => dispatch(updateRefreshToken(data)),
  updateUserId: (data) => dispatch(updateUserId(data)),
});

export default connect(null, mapDispatchToProps)(Login);

// Exact olivv.ai design tokens
const O = {
  cream:       '#fafaf7',
  cream50:     '#fcfcfa',
  cream200:    '#e5ead9',
  forest:      '#053418',
  forest600:   '#0a2818',
  accent:      '#00c853',
  accentHover: '#00a847',
  accentSoft:  '#d6f1d8',
  ink:         '#1a1a1a',
  muted:       '#6b7368',
  muted2:      '#9aa39a',
  line:        '#e6e8df',
  shadowCard:  '0 1px 0 0 rgba(10,40,24,0.04), 0 12px 32px -16px rgba(10,40,24,0.18)',
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: O.cream50,
    '& fieldset': { borderColor: O.line },
    '&:hover fieldset': { borderColor: O.muted2 },
    '&.Mui-focused fieldset': { borderColor: O.forest, borderWidth: '1.5px' },
  },
};

const styles = {
  root: {
    display: 'flex',
    height: '100vh',
    width: '100vw',
    overflow: 'hidden',
    fontFamily: 'GilroyRegular',
  },

  /* ── Left branding panel — olivv.ai forest green ── */
  leftPanel: {
    flex: 1,
    backgroundColor: O.forest,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '60px 56px',
    position: 'relative',
    overflow: 'hidden',
  },
  leftContent: {
    position: 'relative',
    zIndex: 2,
    maxWidth: 420,
  },
  leftLogo: {
    width: 130,
    marginBottom: 48,
    filter: 'brightness(0) invert(1)',
  },
  leftHeading: {
    fontFamily: 'GilroyBold',
    fontSize: 34,
    lineHeight: 1.25,
    color: '#ffffff',
    marginBottom: 16,
  },
  leftSubtext: {
    fontFamily: 'GilroyRegular',
    fontSize: 15,
    color: O.accentSoft,
    lineHeight: 1.65,
    marginBottom: 40,
  },
  divider: {
    width: 40,
    height: 2,
    backgroundColor: O.accentSoft,
    borderRadius: 2,
    marginBottom: 36,
    opacity: 0.3,
  },
  statsRow: {
    display: 'flex',
    gap: 36,
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  statNum: {
    fontFamily: 'GilroyBold',
    fontSize: 22,
    color: O.accent,
  },
  statLabel: {
    fontFamily: 'GilroyRegular',
    fontSize: 12,
    color: O.accentSoft,
    marginTop: 2,
    opacity: 0.7,
  },
  leftGlowOrb1: {
    position: 'absolute',
    width: 360,
    height: 360,
    borderRadius: '50%',
    background: `radial-gradient(circle, rgba(0,200,83,0.12) 0%, transparent 70%)`,
    top: -100,
    right: -100,
    zIndex: 1,
  },
  leftGlowOrb2: {
    position: 'absolute',
    width: 260,
    height: 260,
    borderRadius: '50%',
    background: `radial-gradient(circle, rgba(0,200,83,0.08) 0%, transparent 70%)`,
    bottom: 60,
    left: -40,
    zIndex: 1,
  },

  /* ── Right form panel — olivv.ai cream ── */
  rightPanel: {
    width: '44%',
    minWidth: 420,
    backgroundColor: O.cream,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px 40px',
    position: 'relative',
  },
  formCard: {
    width: '100%',
    maxWidth: 380,
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: '40px 36px',
    boxShadow: O.shadowCard,
    border: `1px solid ${O.line}`,
  },
  formWelcome: {
    fontFamily: 'GilroyBold',
    fontSize: 24,
    color: O.forest,
    marginBottom: 6,
  },
  formSubtitle: {
    fontFamily: 'GilroyRegular',
    fontSize: 14,
    color: O.muted,
    marginBottom: 32,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  fieldWrapper: {
    marginBottom: 18,
  },
  fieldLabel: {
    fontFamily: 'GilroySemiBold',
    fontSize: 13,
    color: O.ink,
    marginBottom: 6,
  },
  inputBase: {
    fontSize: 14,
    fontFamily: 'GilroyRegular',
    color: O.ink,
  },
  errorBox: {
    backgroundColor: '#fff5f5',
    border: '1px solid #ffc9c9',
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: 14,
  },
  errorText: {
    fontFamily: 'GilroySemiBold',
    fontSize: 13,
    color: '#c0392b',
    lineHeight: 1.4,
  },
  submitBtn: {
    backgroundColor: O.accent,
    color: O.forest,
    fontFamily: 'GilroyBold',
    fontSize: 15,
    textTransform: 'none',
    borderRadius: 10,
    padding: '12px 0',
    marginTop: 10,
    letterSpacing: 0.1,
  },
  copyright: {
    position: 'absolute',
    bottom: 24,
    fontFamily: 'GilroyRegular',
    fontSize: 11,
    color: O.muted2,
    textAlign: 'center',
  },
};
